import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import adminService from "../../services/adminService";
import { logout } from "../../services/authService";

import AdminSupport from "../DasboardAdmin/Components/Adminsuporte/Adminsuporte";
import AdminDocuments from "../DasboardAdmin/Components/Admindocuments/Admindocuments";
import AdminUsers from "../DasboardAdmin/Components/Adminusers/Adminusers";
import AdminJobs from "../DasboardAdmin/Components/Adminjobs/Adminjobs";
import AdminSettings from "../DasboardAdmin/Components/Adminsettings/Adminsettings";
import AdminTraining from "../DasboardAdmin/Components/Admintraining/Admintraining";
import AdminEvaluations from "../DasboardAdmin/Components/AdminEvaluations/AdminEvaluations";
import CorporateContracts from "./Components/AdminContratosEmpresarias/AdminCorporateContracts";
import AdminNotifications from "../DasboardAdmin/Components/Adminnotifications/Adminnotifications";
import AdminAttributions from "../DasboardAdmin/Components/AdminAttributions/AdminAttributions";
import AdminFinance from "../DasboardAdmin/Components/AdminFinance/AdminFinance";

import WalletModal from "../../Components/WalletModal/WalletModal";
import { getWallet } from "../../services/walletService";

import "./DashboardAdmin.scss";

const CARDS = [
    {
        key: "suporte",
        label: "Suporte",
        statKey: "openTickets",
        suffix: "Tíquetes",
        variant: "red"
    },
    {
        key: "documentos",
        label: "Documentos",
        statKey: "pendingDocuments",
        suffix: "Pendentes",
        variant: "blue"
    },
    {
        key: "empresas",
        label: "Empresas",
        statKey: "totalCompanies",
        variant: "blue"
    },
    {
        key: "freelancers",
        label: "Freelancers",
        statKey: "totalFreelancers",
        variant: "green"
    },
    {
        key: "vagas-abertas",
        label: "Vagas Abertas",
        statKey: "openJobs",
        variant: "yellow"
    },
    {
        key: "em-andamento",
        label: "Em Andamento",
        statKey: "inProgressJobs",
        variant: "purple"
    },
    {
        key: "finalizadas",
        label: "Finalizadas",
        statKey: "completedJobs",
        variant: "teal"
    },
    {
        key: "avaliacoes",
        label: "Avaliações",
        statKey: null,
        display: "Ver",
        variant: "gold"
    },
    {
        key: "faltas",
        label: "Faltas",
        statKey: "noShowAlerts",
        suffix: "Alertas",
        variant: "red"
    },
    {
        key: "taxa-admin",
        label: "Taxa Admin",
        statKey: "adminBalance",
        isCurrency: true,
        variant: "gold"
    },
    {
        key: "financeiro",
        label: "Financeiro",
        statKey: null,
        display: "Histórico",
        variant: "green"
    },
    {
        key: "contratos-empresariais",
        label: "Contratos Empresariais",
        statKey: null,
        display: "Gerenciar",
        variant: "green"
    },
    {
        key: "notificacoes",
        label: "Notificações",
        statKey: null,
        display: "Central",
        variant: "blue"
    },
    {
        key: "atribuicoes",
        label: "Atribuições",
        statKey: null,
        display: "Consultar",
        variant: "teal"
    },
    {
        key: "ajustes",
        label: "Ajustes",
        statKey: null,
        display: "Geral",
        variant: "gray"
    },
    {
        key: "videos",
        label: "Vídeos",
        statKey: null,
        display: "Aulas",
        variant: "purple"
    }
];

function formatCurrency(value) {
    return (value ?? 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
    });
}

function timeAgo(dateStr) {
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (days < 1) return "hoje";
    if (days === 1) return "1d";
    return `${days}d`;
}

export default function AdminDashboard({ currentUser }) {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [activities, setActivities] = useState([]);
    const [activeCard, setActiveCard] = useState("suporte");
    const [loading, setLoading] = useState(true);
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [wallet, setWallet] = useState(null);
    const [launchGoldRunning, setLaunchGoldRunning] = useState(false);
    const [launchGoldResult, setLaunchGoldResult] = useState(null);

    const handleLogout = async () => {
        const confirmLogout = window.confirm(
            'Tem certeza que deseja sair da conta de Administrador?'
        );

        if (!confirmLogout) return;

        try {
            await logout();
        } catch (error) {
            console.error('Erro ao encerrar sessão:', error);
        } finally {
            localStorage.clear();
            document.cookie.split(";").forEach(c => {
                document.cookie = c
                    .replace(/^ +/, "")
                    .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
            });
            navigate('/', { replace: true });
        }
    };

    const handleActivateLaunchGold = async () => {
        if (launchGoldResult) return;

        const confirmed = window.confirm(
            "Isso vai ativar o Gold Trial de 7 dias para TODOS os freelancers com status pré-cadastro. Essa ação é irreversível. Confirma?"
        );
        if (!confirmed) return;

        setLaunchGoldRunning(true);
        try {
            const data = await adminService.activateLaunchGold();
            setLaunchGoldResult(data.message);
            alert(data.message || "Gold ativado com sucesso!");
        } catch (err) {
            alert(err?.response?.data?.message || "Erro ao ativar o Gold de lançamento.");
        } finally {
            setLaunchGoldRunning(false);
        }
    };

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const [statsData, activitiesData] = await Promise.all([
                adminService.getStats(),
                adminService.getActivities(15)
            ]);
            setStats(statsData);
            setActivities(activitiesData);
        } catch (error) {
            console.error("Erro ao carregar dashboard:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
        loadWallet();
    }, [loadDashboard]);

    async function loadWallet() {
        try {
            const data = await getWallet();
            setWallet(data);
        } catch (err) {
            console.error(err);
        }
    }

    const renderPanel = () => {
        switch (activeCard) {
            case "suporte":
                return <AdminSupport />;
            case "documentos":
                return <AdminDocuments />;
            case "empresas":
                return <AdminUsers userType="companies" title="Empresas" onChanged={loadDashboard} />;
            case "freelancers":
                return <AdminUsers userType="freelancers" title="Freelancers" onChanged={loadDashboard} />;
            case "vagas-abertas":
                return <AdminJobs />;
            case "em-andamento":
                return <AdminJobs />;
            case "finalizadas":
                return <AdminJobs />;
            case "ajustes":
                return <AdminSettings />;
            case "videos":
                return <AdminTraining />;
            case "avaliacoes":
                return <AdminEvaluations />;
            case "contratos-empresariais":
                return <CorporateContracts />;
            case "atribuicoes":
                return <AdminAttributions />;
            case "financeiro":
                return <AdminFinance />;
            case "notificacoes":
                return <AdminNotifications />;
            case "faltas":
                return (
                    <div className="admin-empty-state">
                        Nada por aqui ainda — conecte esse card à sua fonte de dados de faltas.
                    </div>
                );
            default:
                return null;
        }
    };

    const hideActivityPanel = activeCard === "contratos-empresariais" || activeCard === "notificacoes" || activeCard === "financeiro";

    return (
        <div className="admin-dashboard">
            <header className="admin-header">
                <div className="admin-header__profile">
                    <div className="admin-header__avatar">
                        {currentUser?.name?.[0] ?? "A"}
                    </div>
                    <div>
                        <h1>Painel Administrativo</h1>
                        <p>Freelancers — Conta Mestre</p>
                    </div>
                </div>

                <div className="admin-header__actions">
                    <span className="admin-header__role">ADMIN</span>

                    <div
                        className="dashboard-sidebar__info-item dashboard-sidebar__info-item--clickable"
                        onClick={() => setShowWalletModal(true)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && setShowWalletModal(true)}
                    >
                        <span>Saldo:</span>
                        <strong>
                            R$ {((wallet?.balance ?? 0) - (wallet?.reserved ?? 0)).toFixed(2)}
                        </strong>
                    </div>

                    <button
                        className="admin-header__logout-button"
                        onClick={handleLogout}
                    >
                        Sair
                    </button>
                </div>
            </header>

            <div className="admin-stats-grid">
                {CARDS.map((card) => {
                    const value = card.display
                        ? card.display
                        : card.isCurrency
                            ? formatCurrency(stats?.[card.statKey])
                            : stats?.[card.statKey] ?? (loading ? "…" : 0);

                    return (
                        <div
                            key={card.key}
                            className={`
                                admin-stat-card
                                admin-stat-card--${card.variant}
                                ${activeCard === card.key ? "admin-stat-card--active" : ""}
                            `}
                            onClick={() => setActiveCard(card.key)}
                        >
                            <div className="admin-stat-card__label">{card.label}</div>
                            <div className="admin-stat-card__value">{value}</div>
                            {card.suffix && (
                                <div className="admin-stat-card__label">{card.suffix}</div>
                            )}
                        </div>
                    );
                })}
            </div>

            {!hideActivityPanel && (
                <div className="admin-panel">
                    <div className="admin-panel__title">Atividades Recentes</div>
                    <div className="admin-activity-list">
                        {activities.map((activity) => (
                            <div className="admin-activity-list__item" key={activity.id}>
                                <span className="admin-activity-list__tag">{activity.type}</span>
                                <span>{activity.message}</span>
                                <span className="admin-activity-list__time">
                                    {timeAgo(activity.createdAt)}
                                </span>
                            </div>
                        ))}
                        {!loading && activities.length === 0 && (
                            <div className="admin-empty-state">Nenhuma atividade recente.</div>
                        )}
                    </div>
                </div>
            )}

            <div className="admin-panel">{renderPanel()}</div>

            <WalletModal
                isOpen={showWalletModal}
                onClose={() => setShowWalletModal(false)}
                userData={currentUser}
            />
        </div>
    );
}